// src/components/forum/PostDetail.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format, formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { FaTrash } from 'react-icons/fa';
import { CornerCard, GhostButton, PrimaryButton, TextArea, Tag } from '../../components/ui';
import { color, font } from '../../theme/tokens';

// --- 样式 ---
const BackButton = styled(GhostButton)`
    min-height: 40px;
    margin-bottom: 24px;
    padding: 0 18px;
    font-size: 13px;
`;

const DeleteButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    min-height: 36px;
    padding: 0 14px;
    background: transparent;
    color: ${color.bloodHi};
    border: 1px solid rgba(224, 70, 79, 0.5);
    border-radius: 0;
    font-family: ${font.body};
    font-size: 13px;
    letter-spacing: 0.14em;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

    &:hover:not(:disabled) {
        border-color: ${color.bloodHi};
        background: rgba(200, 50, 62, 0.14);
        color: ${color.textBright};
    }

    &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const PostHeader = styled.div`
    padding-bottom: 20px;
    margin-bottom: 24px;
    border-bottom: 1px solid ${color.line};
`;

const HeaderTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 14px;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    min-width: 0;
`;

const PostTitle = styled.h1`
    margin: 0;
    font-family: ${font.display};
    font-weight: 800;
    font-size: 28px;
    line-height: 1.4;
    letter-spacing: 0.08em;
    color: ${color.textBright};
    text-shadow: 0 0 24px rgba(200, 50, 62, 0.3);
    word-break: break-word;
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    font-size: 13px;
    letter-spacing: 0.1em;
    color: ${color.text3};
    b { font-weight: 400; color: ${color.text}; margin-left: 6px; }
    time { color: ${color.text2}; margin-left: 6px; }
`;

// Markdown 渲染区域样式
const MarkdownWrapper = styled.div`
    font-size: 16px;
    line-height: 1.95;
    color: ${color.text};
    word-break: break-word;

    h1, h2, h3 {
        margin-top: 32px;
        padding-bottom: 8px;
        border-bottom: 1px solid ${color.line};
        font-family: ${font.display};
        letter-spacing: 0.08em;
        color: ${color.textBright};
    }
    p { margin: 16px 0; }
    strong { color: ${color.textBright}; }
    a { color: ${color.bloodHi}; text-decoration: none; border-bottom: 1px solid rgba(224, 70, 79, 0.45); &:hover { color: ${color.textBright}; border-color: ${color.textBright}; } }
    code {
        padding: 2px 5px;
        background-color: ${color.surface2};
        font-family: ${font.mono};
        font-size: 0.88em;
    }
    pre > code {
        display: block;
        padding: 14px 16px;
        overflow-x: auto;
        background-color: ${color.void};
        border: 1px solid ${color.line};
        color: ${color.text};
    }
    blockquote {
        margin-left: 0;
        padding: 10px 16px;
        border-left: 2px solid ${color.blood};
        background: rgba(200, 50, 62, 0.06);
        color: ${color.text2};
    }
    img { max-width: 100%; border: 1px solid ${color.line}; }
    ul, ol { padding-left: 22px; }
    hr { border: none; border-top: 1px solid ${color.line}; }
    table { border-collapse: collapse; }
    th, td { border: 1px solid ${color.line}; padding: 6px 10px; }
`;


const CommentSection = styled.section`
    margin-top: 48px;
    padding-top: 8px;
`;

const CommentHeading = styled.h3`
    display: flex;
    align-items: baseline;
    gap: 14px;
    margin: 0 0 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${color.line};
    font-family: ${font.display};
    font-weight: 800;
    font-size: 20px;
    letter-spacing: 0.2em;
    color: ${color.text};
    span { font-family: ${font.body}; font-weight: 400; font-size: 13px; letter-spacing: 0.12em; color: ${color.text3}; }
    b { font-family: ${font.mono}; font-weight: 400; color: ${color.text2}; margin: 0 4px; }
`;

const CommentItem = styled.div`
    position: relative;
    display: flex;
    gap: 16px;
    padding: 18px 20px;
    margin-bottom: 12px;
    background: rgba(7, 2, 5, 0.5);
    border: 1px solid ${color.line};
    transition: border-color 0.2s ease;

    &::before {
        content: '';
        position: absolute;
        left: -1px; top: -1px; bottom: -1px;
        width: 2px;
        background: ${color.blood};
        opacity: 0;
        transition: opacity 0.2s ease;
    }
    &:hover { border-color: ${color.line2}; }
    &:hover::before { opacity: 1; }
`;
const CommentAvatar = styled.img`
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid ${color.line2};
    background: ${color.surface2};
`;
const CommentBody = styled.div`
    flex: 1;
    min-width: 0;
`;
const CommentHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 6px;
    font-size: 13px;
    color: ${color.text3};
    .author { font-family: ${font.display}; font-weight: 700; font-size: 14px; letter-spacing: 0.08em; color: ${color.text}; }
`;
const CommentContent = styled(MarkdownWrapper)`
    font-size: 15px;
    line-height: 1.8;
    color: ${color.text2};
    p { margin: 6px 0; }
`;

const CommentForm = styled.form`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
    margin-top: 28px;
`;

const CommentNotice = styled.p`
    margin: 20px 0 0;
    padding-top: 20px;
    border-top: 1px solid ${color.line};
    font-size: 14px;
    letter-spacing: 0.1em;
    color: ${color.text3};
`;

const LoadState = styled.p`
    margin: 0;
    padding: 48px 0;
    text-align: center;
    font-family: ${font.display};
    font-size: 15px;
    letter-spacing: 0.3em;
    color: ${({ $error }) => ($error ? color.bloodHi : color.text3)};
`;

// --- 组件 ---
const PostDetail = ({ postId, onBack, onDeleteSuccess }) => {
    const { user } = useAuth();
    const [postData, setPostData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false); 
    const [error, setError] = useState('');
    const [commentContent, setCommentContent] = useState('');

    const fetchDetails = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await apiClient.get(`/public/forum/posts/${postId}`);
            setPostData(response.data);
        } catch (err) {
            console.error("Failed to fetch post details:", err);
            setError('帖子加载失败，请稍后重试。');
        } finally {
            setIsLoading(false);
        }
    };
    
    useEffect(() => {
        fetchDetails();
    }, [postId]);

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!commentContent.trim()) return;

        try {
            await apiClient.post(`/user/forum/posts/${postId}/comments`, { content: commentContent });
            setCommentContent('');
            fetchDetails(); 
        } catch (err) {
            alert('评论发送失败：' + (err.response?.data?.message || '请检查网络连接'));
        }
    };

    const handleDeletePost = async () => {
        if (!window.confirm('帖子将被永久删除，确认继续吗？')) {
            return;
        }
        setIsDeleting(true);
        try {
            const response = await apiClient.delete(`/user/forum/posts/${postId}`);
            alert(response.data.message);
            onDeleteSuccess(); 
        } catch (err) {
            alert('删除失败：' + (err.response?.data?.message || '未知错误'));
            setIsDeleting(false);
        }
    };
    
    const isAdmin = user && ['site_owner', 'admin'].includes(user.role);
    const canComment = user && (user.level > 0 || isAdmin);

    if (isLoading) return <LoadState>载入中…</LoadState>;
    if (error) return <LoadState $error role="alert">{error}</LoadState>;
    if (!postData) return null;

    const { post, comments } = postData;
    const canDelete = user && (user.id === post.user_id || isAdmin);

    return (
        <>
            <BackButton type="button" onClick={onBack}>← 返回列表</BackButton>
            <CornerCard pad="36px 40px">
                <PostHeader>
                    <HeaderTop>
                        <TitleContainer>
                            {post.is_official && <Tag $solid>官方</Tag>}
                            <PostTitle>{post.title}</PostTitle>
                        </TitleContainer>
                        {canDelete && (
                            <DeleteButton type="button" onClick={handleDeletePost} disabled={isDeleting}>
                                <FaTrash size={12} aria-hidden="true" />
                                {isDeleting ? '删除中…' : '删除帖子'}
                            </DeleteButton>
                        )}
                    </HeaderTop>

                    <Meta>
                        <span>作者<b>{post.author_nickname || '匿名'}</b></span>
                        <span>
                            发布于
                            <time dateTime={post.created_at} title={format(new Date(post.created_at), 'yyyy-MM-dd HH:mm:ss')}>
                                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: zhCN })}
                            </time>
                        </span>
                    </Meta>
                </PostHeader>

                <MarkdownWrapper>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {post.content}
                    </ReactMarkdown>
                </MarkdownWrapper>

                <CommentSection>
                    <CommentHeading>回応<span>共<b>{comments.length}</b>条评论</span></CommentHeading>
                    {comments.map(comment => (
                        <CommentItem key={comment.id}>
                            <CommentAvatar src={comment.author_avatar_url || '/default_avatar.png'} alt=""/>
                            <CommentBody>
                                <CommentHeader>
                                    <span className="author">{comment.author_nickname || '匿名'}</span>
                                    <time dateTime={comment.created_at} title={format(new Date(comment.created_at), 'yyyy-MM-dd HH:mm:ss')}>
                                        {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: zhCN })}
                                    </time>
                                </CommentHeader>
                                <CommentContent>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {comment.content}
                                    </ReactMarkdown>
                                </CommentContent>
                            </CommentBody>
                        </CommentItem>
                    ))}

                    {canComment ? (
                        <CommentForm onSubmit={handleCommentSubmit}>
                            <TextArea
                                value={commentContent}
                                onChange={(e) => setCommentContent(e.target.value)}
                                placeholder="写下你的评论…（支持 Markdown）"
                                aria-label="评论内容"
                                style={{ minHeight: 110 }}
                                required
                            />
                            <PrimaryButton type="submit">发表评论</PrimaryButton>
                        </CommentForm>
                    ) : (
                        <CommentNotice>
                            权限不足：等级需大于 0 才能发表评论。
                        </CommentNotice>
                    )}
                </CommentSection>
            </CornerCard>
        </>
    );

};

export default PostDetail;